import { memo, useCallback, useEffect, useMemo } from "react";
import { observer, useSelector } from "@legendapp/state/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  firefliesUi$,
  setActiveBatchResult,
  setClickUpSubmitting,
  setSelectedClickUpListId,
  setSelectedClickUpTeamId,
  toggleBatchResultClickUpSelection,
  toggleAllBatchResultsClickUpSelection,
  updateBatchResultAssignee,
  updateBatchResultTitle,
} from "@/state/firefliesUiLegend";
import { FirefliesClickUpPreviewPanel } from "./FirefliesClickUpPreviewPanel";
import {
  useClickUpLists,
  useClickUpMembers,
  useClickUpTeams,
  useCreateClickUpTasks,
} from "@/hooks/useClickUp";
import { getMemberDisplayName } from "@/lib/clickup/assigneeRouting";
import type { CreateClickUpTaskRequest } from "@/types/clickup";

export const FirefliesBatchResultsDialog = memo(
  observer(function FirefliesBatchResultsDialog() {
    const { toast } = useToast();
    const batchOpen = useSelector(() => firefliesUi$.batchResultsOpen.get());
    const batchResults = useSelector(() => firefliesUi$.batchResults.get());
    const activeId = useSelector(() => firefliesUi$.activeBatchResultId.get());
    const batchProcessing = useSelector(() => firefliesUi$.batchProcessing.get());
    const selectedTeamId = useSelector(() => firefliesUi$.selectedClickUpTeamId.get());
    const selectedListId = useSelector(() => firefliesUi$.selectedClickUpListId.get());
    const clickUpSubmitting = useSelector(() => firefliesUi$.clickUpSubmitting.get());

    const dialogReady = batchOpen && !batchProcessing?.active;
    const { data: teams = [], isLoading: teamsLoading, error: teamsError } = useClickUpTeams(dialogReady);
    const { data: members = [], isLoading: membersLoading } = useClickUpMembers(
      selectedTeamId,
      dialogReady && !!selectedTeamId
    );
    const { data: lists = [], isLoading: listsLoading } = useClickUpLists(
      selectedTeamId,
      dialogReady && !!selectedTeamId
    );
    const createTasksMutation = useCreateClickUpTasks();

    useEffect(() => {
      if (!dialogReady || teamsLoading || teams.length === 0) return;
      if (!selectedTeamId) {
        setSelectedClickUpTeamId(teams[0].id);
      }
    }, [dialogReady, teamsLoading, teams, selectedTeamId]);

    const activeResult = useMemo(
      () => batchResults.find((r) => r.transcriptId === activeId),
      [batchResults, activeId]
    );

    const successResults = useMemo(
      () => batchResults.filter((r) => r.status === "success"),
      [batchResults]
    );

    const selectedItems = useMemo(
      () => successResults.filter((r) => r.selectedForClickUp),
      [successResults]
    );

    const selectedCount = selectedItems.length;

    const allSuccessSelected =
      successResults.length > 0 &&
      successResults.every((r) => r.selectedForClickUp);

    const getAssigneeName = useCallback(
      (assigneeId: number | null) => {
        if (assigneeId == null) return null;
        const member = members.find((m) => m.id === assigneeId);
        return member?.username || getMemberDisplayName(assigneeId);
      },
      [members]
    );

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open && (batchProcessing?.active || clickUpSubmitting)) return;
        firefliesUi$.batchResultsOpen.set(open);
      },
      [batchProcessing?.active, clickUpSubmitting]
    );

    const handlePushToClickUp = useCallback(async () => {
      if (selectedCount === 0) {
        toast({
          title: "No meetings selected",
          description: "Select at least one formatted meeting to send to ClickUp.",
          variant: "destructive",
        });
        return;
      }

      if (!selectedListId) {
        toast({
          title: "Select a ClickUp list",
          description: "Choose the target list where parent tasks will be created.",
          variant: "destructive",
        });
        return;
      }

      const missingAssignee = selectedItems.find(
        (item) => (item.assigneeId ?? item.suggestedAssigneeId) == null
      );
      if (missingAssignee) {
        toast({
          title: "Assignee required",
          description: `Select an assignee for "${missingAssignee.meetingTitle}".`,
          variant: "destructive",
        });
        return;
      }

      const tasks: CreateClickUpTaskRequest[] = selectedItems.map((item) => ({
        listId: selectedListId,
        name: item.editableTitle.trim() || item.notes!.parentTaskTitle,
        markdown_description: item.notes!.parentTaskDescription,
        assignees: [item.assigneeId ?? item.suggestedAssigneeId!],
        checklistItems: item.notes!.checklistItems,
      }));

      setClickUpSubmitting(true);
      try {
        const results = await createTasksMutation.mutateAsync(tasks);
        const succeeded = results.filter((r) => r.success);
        const failed = results.filter((r) => !r.success);

        if (succeeded.length > 0) {
          toast({
            title: "Sent to ClickUp",
            description: `${succeeded.length} task${succeeded.length !== 1 ? "s" : ""} created successfully.`,
          });
        }

        if (failed.length > 0) {
          toast({
            title: "Some tasks failed",
            description: failed.map((f) => (!f.success ? f.error : "")).filter(Boolean).join("; "),
            variant: "destructive",
          });
        }

        if (failed.length === 0) {
          handleOpenChange(false);
        }
      } catch (e) {
        toast({
          title: "ClickUp push failed",
          description: e instanceof Error ? e.message : "Could not create tasks",
          variant: "destructive",
        });
      } finally {
        setClickUpSubmitting(false);
      }
    }, [
      selectedCount,
      selectedListId,
      selectedItems,
      createTasksMutation,
      toast,
      handleOpenChange,
    ]);

    if (!batchOpen && !batchProcessing?.active) return null;

    const isProcessing = batchProcessing?.active ?? false;
    const completed = batchProcessing?.completed ?? batchResults.length;
    const total = batchProcessing?.total ?? batchResults.length;
    const isSubmitting = clickUpSubmitting || createTasksMutation.isPending;

    return (
      <Dialog open={batchOpen || isProcessing} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>
              {isProcessing
                ? `Formatting meetings (${completed} of ${total})`
                : `Formatted meetings (${successResults.length} ready)`}
            </DialogTitle>
            <DialogDescription>
              {isProcessing
                ? `Processing: ${batchProcessing?.currentLabel || "…"}`
                : "Review each meeting, assign owners, then push selected tasks to ClickUp."}
            </DialogDescription>
            {teamsError && !isProcessing && (
              <p className="text-sm text-destructive pt-1">
                {teamsError instanceof Error ? teamsError.message : "Failed to load ClickUp"}
              </p>
            )}
            {isProcessing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                <span className="truncate">Please wait — meetings are formatted one at a time.</span>
              </div>
            )}
          </DialogHeader>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            <aside className="w-[min(280px,38%)] border-r flex flex-col min-h-0 bg-muted/20">
              {successResults.length > 0 && !isProcessing && (
                <div className="flex items-center gap-2 px-3 py-2 border-b text-sm">
                  <Checkbox
                    id="batch-select-all-clickup"
                    checked={allSuccessSelected}
                    onCheckedChange={(checked) =>
                      toggleAllBatchResultsClickUpSelection(checked === true)
                    }
                  />
                  <label
                    htmlFor="batch-select-all-clickup"
                    className="cursor-pointer text-muted-foreground"
                  >
                    Select all for ClickUp
                  </label>
                </div>
              )}

              <ul className="flex-1 overflow-y-auto p-2 space-y-1">
                {batchResults.map((item) => {
                  const isActive = item.transcriptId === activeId;
                  const isCurrent =
                    isProcessing &&
                    batchProcessing?.currentTranscriptId === item.transcriptId;
                  const assigneeName = getAssigneeName(
                    item.assigneeId ?? item.suggestedAssigneeId
                  );

                  return (
                    <li key={item.transcriptId}>
                      <div
                        className={cn(
                          "flex items-start gap-2 rounded-md border p-2 text-sm transition-colors",
                          isActive && "border-primary bg-primary/5",
                          !isActive && "hover:bg-muted/60",
                          item.status === "failed" && "border-destructive/30"
                        )}
                      >
                        {item.status === "success" ? (
                          <Checkbox
                            className="mt-0.5"
                            checked={item.selectedForClickUp}
                            disabled={isProcessing}
                            onCheckedChange={() =>
                              toggleBatchResultClickUpSelection(item.transcriptId)
                            }
                            aria-label={`Select ${item.meetingTitle} for ClickUp`}
                          />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                        )}

                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => setActiveBatchResult(item.transcriptId)}
                          disabled={item.status === "failed" && !item.error}
                        >
                          <p className="font-medium truncate leading-tight">
                            {item.meetingTitle}
                          </p>
                          {item.status === "success" && item.notes && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {item.editableTitle || item.notes.parentTaskTitle}
                            </p>
                          )}
                          {item.status === "success" && assigneeName && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              → {assigneeName}
                            </p>
                          )}
                          {item.status === "failed" && (
                            <p className="text-xs text-destructive mt-0.5 line-clamp-2">
                              {item.error || "Format failed"}
                            </p>
                          )}
                          {isCurrent && (
                            <span className="inline-flex items-center gap-1 text-xs text-primary mt-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Formatting…
                            </span>
                          )}
                          {item.status === "success" && !isCurrent && (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-1">
                              <CheckCircle2 className="h-3 w-3 text-green-600" />
                              Ready
                            </span>
                          )}
                        </button>
                      </div>
                    </li>
                  );
                })}

                {isProcessing &&
                  batchProcessing?.currentTranscriptId &&
                  !batchResults.some(
                    (r) => r.transcriptId === batchProcessing.currentTranscriptId
                  ) && (
                    <li className="rounded-md border border-dashed p-3 text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      <span className="truncate">{batchProcessing.currentLabel}</span>
                    </li>
                  )}
              </ul>
            </aside>

            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              {activeResult?.status === "success" && activeResult.notes ? (
                <FirefliesClickUpPreviewPanel
                  notes={activeResult.notes}
                  editableTitle={activeResult.editableTitle}
                  onTitleChange={(title) =>
                    updateBatchResultTitle(activeResult.transcriptId, title)
                  }
                  members={members}
                  membersLoading={membersLoading || teamsLoading}
                  assigneeId={activeResult.assigneeId}
                  suggestedAssigneeId={activeResult.suggestedAssigneeId}
                  assigneeReason={activeResult.assigneeReason}
                  onAssigneeChange={(assigneeId) =>
                    updateBatchResultAssignee(activeResult.transcriptId, assigneeId)
                  }
                />
              ) : activeResult?.status === "failed" ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12 px-4">
                  <AlertCircle className="h-10 w-10 text-destructive mb-3" />
                  <p className="font-medium">Could not format this meeting</p>
                  <p className="text-sm text-muted-foreground mt-2 max-w-md">
                    {activeResult.error || "Unknown error"}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-12">
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-8 w-8 animate-spin mb-3" />
                      <p className="text-sm">Waiting for first result…</p>
                    </>
                  ) : (
                    <p className="text-sm">Select a meeting from the list to preview.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isProcessing && (
            <div className="px-6 py-3 border-t shrink-0 flex flex-wrap gap-3 items-end bg-muted/10">
              {teams.length > 1 && (
                <div className="space-y-1 min-w-[160px]">
                  <label className="text-xs font-medium text-muted-foreground">Team</label>
                  <Select
                    value={selectedTeamId ?? ""}
                    onValueChange={(v) => setSelectedClickUpTeamId(v || null)}
                    disabled={teamsLoading || isSubmitting}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1 flex-1 min-w-[220px]">
                <label className="text-xs font-medium text-muted-foreground">Target list</label>
                <Select
                  value={selectedListId ?? ""}
                  onValueChange={(v) => setSelectedClickUpListId(v || null)}
                  disabled={listsLoading || !selectedTeamId || isSubmitting}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue
                      placeholder={
                        listsLoading
                          ? "Loading lists…"
                          : !selectedTeamId
                            ? "Select team first"
                            : lists.length === 0
                              ? "No lists found"
                              : "Select ClickUp list"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {lists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.path}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isProcessing || isSubmitting}
            >
              {isProcessing ? "Formatting…" : "Close"}
            </Button>
            {!isProcessing && (
              <Button
                onClick={() => void handlePushToClickUp()}
                disabled={selectedCount === 0 || isSubmitting || !selectedListId}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Confirm &amp; Push to ClickUp
                {selectedCount > 0 ? ` (${selectedCount})` : ""}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  })
);
